import React, { useState, useEffect } from "react";
import Button from "../../../components/common/Button";
import { MdAdd } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import PostsTable from "components/community/PostsTable";
import PostModal from "components/community/PostModal";
import Pagination from "components/common/Pagination";
import DeleteConfirmationModal from "components/modals/DeleteConfirmationModal";
import ViewSwitcher, { ViewType } from "components/community/ViewSwitcher";
import ReportedPostsTable from "components/community/ReportedPostsTable";
import { usePosts, usePostOperations } from "hooks/usePosts";
import { useReportedPosts } from "hooks/useReportedPosts";
import {
  Post,
  CreatePostInput,
  UpdatePostInput,
  PostType,
} from "types/community";

const Community: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeView, setActiveView] = useState<ViewType>("posts");

  // Get initial page and filter from URL query params
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialFilter = searchParams.get("filter") as PostType | null;
  const initialChallengeId = searchParams.get("challenge_id")
    ? parseInt(searchParams.get("challenge_id")!, 10)
    : null;
  const initialReportedPage = parseInt(
    searchParams.get("reportedPage") || "1",
    10
  );

  const {
    posts,
    loading,
    error,
    total,
    page,
    limit,
    totalPages,
    filterType,
    challengeId,
    refresh,
    changePage,
    setTypeFilter,
    setChallengeFilter,
  } = usePosts(initialPage, initialFilter, initialChallengeId);
  const {
    loading: operationLoading,
    error: operationError,
    createPost,
    updatePost,
    deletePost,
    likePost,
    unlikePost,
    pinPost,
    unpinPost,
    clearError,
  } = usePostOperations();

  const {
    reportedPosts,
    loading: reportedLoading,
    error: reportedError,
    total: reportedTotal,
    page: reportedPage,
    limit: reportedLimit,
    totalPages: reportedTotalPages,
    refresh: refreshReportedPosts,
    changePage: changeReportedPage,
  } = useReportedPosts(initialReportedPage);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Post | null>(null);

  // Update URL when page or filter changes
  useEffect(() => {
    const params: Record<string, string> = {};

    if (activeView === "posts") {
      params.page = page.toString();

      if (filterType) {
        params.filter = filterType;
      }

      if (challengeId) {
        params.challenge_id = challengeId.toString();
      }
    } else {
      params.reportedPage = reportedPage.toString();
    }

    setSearchParams(params, { replace: true });
  }, [
    page,
    filterType,
    challengeId,
    reportedPage,
    activeView,
    setSearchParams,
  ]);

  const handleCreatePost = () => {
    setEditingPost(null);
    setIsModalOpen(true);
  };

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
    setIsModalOpen(true);
  };

  const handleDeletePost = (post: Post) => {
    setShowDeleteConfirm(post);
  };

  const handleLikePost = async (post: Post) => {
    const result = await likePost(post.id);
    if (result.success) {
      refresh();
    }
  };

  const handleUnlikePost = async (post: Post) => {
    const result = await unlikePost(post.id);
    if (result.success) {
      refresh();
    }
  };

  const handlePinPost = async (post: Post) => {
    const success = await pinPost(post.id);
    if (success) {
      refresh();
    }
  };

  const handleUnpinPost = async (post: Post) => {
    const success = await unpinPost(post.id);
    if (success) {
      refresh();
    }
  };

  const handleModalSubmit = async (data: CreatePostInput | UpdatePostInput) => {
    if (editingPost) {
      const updatedPost = await updatePost(
        editingPost.id,
        data as UpdatePostInput
      );
      if (updatedPost) {
        refresh();
        if (activeView === "reported") {
          refreshReportedPosts();
        }
        handleModalClose();
      }
    } else {
      const newPost = await createPost(data as CreatePostInput);
      if (newPost) {
        refresh();
        handleModalClose();
      }
    }
  };

  const confirmDelete = async () => {
    if (showDeleteConfirm) {
      const success = await deletePost(showDeleteConfirm.id);
      if (success) {
        refresh();
        if (activeView === "reported") {
          refreshReportedPosts();
        }
      }
      setShowDeleteConfirm(null);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPost(null);
    clearError();
  };

  const handleFilterChange = (type: PostType | null) => {
    setTypeFilter(type);
  };

  const handleChallengeChange = (id: number | null) => {
    setChallengeFilter(id);
  };

  return (
    <div className="grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-fit w-full">
        <div className="mb-6 mt-6 flex items-center justify-between">
          <ViewSwitcher activeView={activeView} onViewChange={setActiveView} />
          {activeView === "posts" && (
            <Button onClick={handleCreatePost}>
              <MdAdd className="mr-2 h-4 w-4" />
              <span>Create Post</span>
            </Button>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/50 dark:text-red-200">
            {error}
          </div>
        )}

        {operationError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/50 dark:text-red-200">
            {operationError}
          </div>
        )}

        {reportedError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/50 dark:text-red-200">
            {reportedError}
          </div>
        )}

        {activeView === "posts" ? (
          <>
            <PostsTable
              posts={posts}
              loading={loading}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
              onLike={handleLikePost}
              onUnlike={handleUnlikePost}
              onPin={handlePinPost}
              onUnpin={handleUnpinPost}
              activeFilter={filterType}
              onFilterChange={handleFilterChange}
              challengeId={challengeId}
              onChallengeChange={handleChallengeChange}
            />

            <Pagination
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              entityName="posts"
              isSearchMode={!!filterType}
              onPageChange={changePage}
              loading={loading}
            />
          </>
        ) : (
          <>
            <ReportedPostsTable
              reportedPosts={reportedPosts}
              loading={reportedLoading}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
            />

            <Pagination
              page={reportedPage}
              limit={reportedLimit}
              total={reportedTotal}
              totalPages={reportedTotalPages}
              entityName="reported posts"
              isSearchMode={false}
              onPageChange={changeReportedPage}
              loading={reportedLoading}
            />
          </>
        )}
      </div>

      <PostModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        post={editingPost}
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        itemName={showDeleteConfirm?.content.substring(0, 50) || ""}
        loading={operationLoading}
        error={operationError}
      />
    </div>
  );
};

export default Community;
